// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminCommissions\AdminCommissions.jsx
// Название: Admin Commissions Management
// Версия: 1.0.0
// Дата: 26.01.2026

import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import './AdminCommissions.css';

const AdminCommissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState({ 
    totalRevenue: 0,
    directRevenue: 0,
    referralRevenue: 0,
    subscriptionRevenue: 0,
    thisMonth: 0,
    lastMonth: 0,
    pendingPayout: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/commissions');
      if (res.data.success) {
        setCommissions(res.data.commissions || []);
        setStats(res.data.stats || {});
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      direct_lead: { label: 'Прямой лид', class: 'type-direct', icon: '🎁', amount: '€7' },
      referral_lead: { label: 'Реферал', class: 'type-referral', icon: '👥', amount: '€5' },
      subscription: { label: 'Подписка', class: 'type-subscription', icon: '💎', amount: '—' }
    };
    const badge = badges[type] || { label: type, class: 'type-default', icon: '💰', amount: '—' };
    return <span className={`type-badge ${badge.class}`}>{badge.icon} {badge.label}</span>;
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: { label: 'Получено', class: 'badge-success' },
      pending: { label: 'Ожидает', class: 'badge-warning' },
      failed: { label: 'Ошибка', class: 'badge-danger' }
    };
    const badge = badges[status] || { label: status, class: 'badge-default' };
    return <span className={`admin-badge ${badge.class}`}>{badge.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEur = (amount) => {
    const num = parseFloat(amount) || 0;
    return `€${num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const filteredCommissions = commissions.filter(comm => {
    // Type filter
    if (filter !== 'all' && comm.type !== filter) return false;
    
    // Period filter
    if (period !== 'all') {
      const commDate = new Date(comm.created_at);
      const now = new Date();
      
      if (period === 'today') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (commDate < today) return false;
      } else if (period === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (commDate < weekAgo) return false;
      } else if (period === 'month') {
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        if (commDate < monthAgo) return false;
      }
    }
    
    return true;
  });

  const growth = stats.lastMonth > 0 
    ? (((stats.thisMonth - stats.lastMonth) / stats.lastMonth) * 100).toFixed(0)
    : stats.thisMonth > 0 ? 100 : 0;

  if (loading) {
    return (
      <div className="admin-commissions">
        <div className="admin-loading">⏳ Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="admin-commissions">
      <div className="admin-page-header">
        <h1>📊 Комиссии</h1>
        <p>Доходы платформы DARY</p>
      </div>

      {/* Revenue Card */}
      <div className="revenue-hero">
        <div className="revenue-main">
          <div className="revenue-icon">💰</div>
          <div className="revenue-info">
            <span className="revenue-label">Общий доход</span>
            <span className="revenue-value">{formatEur(stats.totalRevenue)}</span>
          </div>
        </div>
        <div className="revenue-breakdown">
          <div className="revenue-item">
            <span className="revenue-item-icon">🎁</span>
            <span className="revenue-item-label">Прямые лиды (€7)</span>
            <span className="revenue-item-value">{formatEur(stats.directRevenue)}</span>
          </div>
          <div className="revenue-item">
            <span className="revenue-item-icon">👥</span>
            <span className="revenue-item-label">Рефералы (€5)</span>
            <span className="revenue-item-value">{formatEur(stats.referralRevenue)}</span>
          </div>
          <div className="revenue-item">
            <span className="revenue-item-icon">💎</span>
            <span className="revenue-item-label">Подписки</span>
            <span className="revenue-item-value">{formatEur(stats.subscriptionRevenue)}</span>
          </div>
        </div>
      </div>

      {/* Period Stats */}
      <div className="period-stats-grid">
        <div className="period-card">
          <div className="period-header">
            <span className="period-title">Этот месяц</span>
            {growth > 0 && <span className="growth-badge positive">+{growth}%</span>}
            {growth < 0 && <span className="growth-badge negative">{growth}%</span>}
          </div>
          <span className="period-value">{formatEur(stats.thisMonth)}</span>
        </div>
        <div className="period-card">
          <div className="period-header">
            <span className="period-title">Прошлый месяц</span>
          </div>
          <span className="period-value">{formatEur(stats.lastMonth)}</span>
        </div>
        <div className="period-card pending">
          <div className="period-header">
            <span className="period-title">К выплате бизнесам</span>
          </div>
          <span className="period-value">{formatEur(stats.pendingPayout)}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-toolbar">
        <div className="admin-filters">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Все типы
          </button>
          <button 
            className={`filter-btn direct ${filter === 'direct_lead' ? 'active' : ''}`}
            onClick={() => setFilter('direct_lead')}
          >
            🎁 Прямые
          </button>
          <button 
            className={`filter-btn referral ${filter === 'referral_lead' ? 'active' : ''}`}
            onClick={() => setFilter('referral_lead')}
          >
            👥 Рефералы
          </button>
          <button 
            className={`filter-btn subscription ${filter === 'subscription' ? 'active' : ''}`}
            onClick={() => setFilter('subscription')}
          >
            💎 Подписки
          </button>
        </div>
        <div className="period-filters">
          <button 
            className={`period-btn ${period === 'all' ? 'active' : ''}`}
            onClick={() => setPeriod('all')}
          >
            Всё время
          </button>
          <button 
            className={`period-btn ${period === 'today' ? 'active' : ''}`}
            onClick={() => setPeriod('today')}
          >
            Сегодня
          </button>
          <button 
            className={`period-btn ${period === 'week' ? 'active' : ''}`}
            onClick={() => setPeriod('week')}
          >
            Неделя
          </button>
          <button 
            className={`period-btn ${period === 'month' ? 'active' : ''}`}
            onClick={() => setPeriod('month')}
          >
            Месяц
          </button>
        </div>
      </div>

      {/* Commissions Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Тип</th>
              <th>Сумма</th>
              <th>Бизнес</th>
              <th>Кампания</th>
              <th>Лид</th>
              <th>Статус</th>
              <th>Дата</th>
            </tr>
          </thead>
          <tbody>
            {filteredCommissions.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-row">Нет комиссий</td>
              </tr>
            ) : (
              filteredCommissions.map(comm => (
                <tr key={comm.id}>
                  <td>#{comm.id}</td>
                  <td>{getTypeBadge(comm.type)}</td>
                  <td>
                    <span className="commission-amount">{formatEur(comm.amount)}</span>
                  </td>
                  <td>
                    <div className="business-cell">
                      <span className="business-email">{comm.business_email || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <span className="campaign-name">{comm.campaign_name || '—'}</span>
                  </td>
                  <td>
                    {comm.lead_email ? (
                      <span className="lead-email">{comm.lead_email}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>{getStatusBadge(comm.status)}</td>
                  <td className="text-muted">{formatDate(comm.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCommissions;
